import { Suspense, lazy } from "react";
// BrowserRouter with an explicit SPA fallback in the security-headers
// extension (see `SPA_FALLBACK_PATHS` + `SPA_FALLBACK_PREFIXES`). The
// fallback is scoped to our known SPA route shapes so Harper's Resource
// URLs (/Customer, /AuthClerk, /ValidateCrateScan, ...) still take priority.
// We moved off HashRouter because Clerk's <SignIn /> compares
// `window.location.pathname` against its configured signInUrl, and hash-
// only routing makes pathname always "/" — Clerk infinitely redirects to
// what it thinks is the sign-in URL.
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SessionRestoreScreen } from "./components/SessionRestoreScreen";
import { ApiClientSetup } from "./components/ApiClientSetup";
import { MutationQueueInstaller } from "./components/MutationQueueInstaller";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { LoadingFallback } from "./components/LoadingFallback";
import { TopNav } from "./components/Layout/TopNav";
import { ScrollToTop } from "./components/ScrollToTop";
import { BottomTabBar } from "./components/Layout/BottomTabBar";
// WP-009 Story 3: session expired overlay (kept for WP-009 backward-compat tests)
import { SessionExpiredOverlay } from "./components/SessionExpiredOverlay";
// WP-011 Story 1: production session expired modal with exact AC-003 copy (AC-001, AC-002, AC-003)
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import { NativeDeepLinkBridge } from "./components/NativeDeepLinkBridge";
import { NativePushBridge } from "./components/NativePushBridge";
// WP-011 Story 2: form draft registry context (AC-005)
import { FormDraftProvider } from "./context/FormDraftContext";
import { useAuth } from "./context/AuthContext";
// WP-ATTICDEV-032-DRIVER-005: driver route guard (AC-006, AC-008)
import { DriverRoleRoute } from "./components/DriverRoleRoute";
// Admin route guard
import { AdminRoute } from "./components/AdminRoute";
// Customer-only route guard — blocks drivers/admins from customer pages.
import { CustomerRoleRoute } from "./components/CustomerRoleRoute";
// Onboarding gate — customers without address+card+first-crate go to /welcome.
import { OnboardingCompleteRoute } from "./components/OnboardingCompleteRoute";
import { ConsentGate } from "./components/legal/ConsentGate";
import { MobileOnlyRoute } from "./components/MobileOnlyRoute";
// WelcomeFlow is imported EAGERLY (not lazy): the gate redirects to /welcome
// right as the sign-in is still settling, and a lazy Suspense boundary mounting
// there raced the auth-settle re-renders in headless CI — the flow got torn
// down before it stabilized and never appeared. Eager import removes the
// Suspense boundary so /welcome renders synchronously the instant we land on it.
import { WelcomeFlow } from "./components/onboarding/WelcomeFlow";
// Role-aware landing redirect for `/`.
import { RoleLandingRedirect } from "./components/RoleLandingRedirect";
// WP-ATTICDEV-013-SNACKBAR: global snackbar system (US-001)
import { SnackbarProvider } from "./context/SnackbarContext";
import { HarperWSProvider } from "./context/HarperWSContext";
// WP-ATTICDEV-032-DRIVER-004: role-change event bus for reactive nav tab updates (AC-005)
import { RoleVersionProvider } from "./context/RoleVersionContext";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { SyncIndicator } from "./components/SyncIndicator";
import { ReporterInstaller } from "./reporting/ReporterInstaller";
// Clerk glue: bridge Clerk sessions to AuthContext; a dedicated route
// for OAuth-redirect return handshakes.
import { ClerkBridge } from "./components/ClerkBridge";
import { ReferralRedeemer } from "./components/ReferralRedeemer";
import { AuthenticateWithRedirectCallback } from "@clerk/clerk-react";

/**
 * Retry a dynamic import once; if it still fails, force a full reload.
 * Mitigates stale-bundle errors: when we deploy, Vite rotates asset hashes,
 * and a client with a cached index.html ends up trying to import a chunk
 * the server no longer has. A reload fetches the fresh index.html (now
 * no-cache) which points at the current hashes.
 */
function lazyRetry<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch(() =>
    factory().catch(() => {
      if (typeof window !== 'undefined') window.location.reload();
      // Return value is unreachable — reload() navigates away — but the
      // promise type demands something so we yield a never-resolving promise.
      return new Promise<T>(() => {});
    }),
  );
}

const LoginPage = lazy(() => lazyRetry(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage }))));
const DashboardPage = lazy(() => lazyRetry(() => import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))));
const ProfileScreen = lazy(() => lazyRetry(() => import("./pages/ProfileScreen").then((m) => ({ default: m.ProfileScreen }))));
// Profile subpages — see docs/handoff_profile/PROFILE_HUB.md.
const ProfilePersonal = lazy(() => lazyRetry(() => import("./pages/profile/Personal").then((m) => ({ default: m.ProfilePersonal }))));
const ProfilePassword = lazy(() => lazyRetry(() => import("./pages/profile/Password").then((m) => ({ default: m.ProfilePassword }))));
const ProfileRefer = lazy(() => lazyRetry(() => import("./pages/profile/Refer").then((m) => ({ default: m.ProfileRefer }))));
const ProfileAddress = lazy(() => lazyRetry(() => import("./pages/profile/Address").then((m) => ({ default: m.ProfileAddress }))));
const ProfilePreferences = lazy(() => lazyRetry(() => import("./pages/profile/Preferences").then((m) => ({ default: m.ProfilePreferences }))));
const ProfilePayment = lazy(() => lazyRetry(() => import("./pages/profile/Payment").then((m) => ({ default: m.ProfilePayment }))));
const ProfilePaymentAdd = lazy(() => lazyRetry(() => import("./pages/profile/AddPaymentMethod").then((m) => ({ default: m.AddPaymentMethod }))));
const ProfileNotifications = lazy(() => lazyRetry(() => import("./pages/profile/Notifications").then((m) => ({ default: m.ProfileNotifications }))));
const ProfileHelp = lazy(() => lazyRetry(() => import("./pages/profile/Help").then((m) => ({ default: m.ProfileHelp }))));
const ProfileLegal = lazy(() => lazyRetry(() => import("./pages/profile/Legal").then((m) => ({ default: m.ProfileLegal }))));
const ProfileDelete = lazy(() => lazyRetry(() => import("./pages/profile/Delete").then((m) => ({ default: m.ProfileDelete }))));
const ProfileClose = lazy(() => lazyRetry(() => import("./pages/profile/CloseAccount").then((m) => ({ default: m.ProfileClose }))));
const CrateDetailScreen = lazy(() => lazyRetry(() => import("./pages/CrateDetailScreen").then((m) => ({ default: m.CrateDetailScreen }))));
const CameraHelpPage = lazy(() => lazyRetry(() => import("./pages/CameraHelpPage").then((m) => ({ default: m.CameraHelpPage }))));
// MyStoragePage merged into DashboardPage
const ForgotPasswordPage = lazy(() => lazyRetry(() => import("./pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage }))));
const DriverActivePage = lazy(() => lazyRetry(() => import("./pages/DriverActivePage").then((m) => ({ default: m.DriverActivePage }))));
const DriverHomePage = lazy(() => lazyRetry(() => import("./pages/DriverHomePage").then((m) => ({ default: m.DriverHomePage }))));
const DriverLinkCratePage = lazy(() => lazyRetry(() => import("./pages/DriverLinkCratePage").then((m) => ({ default: m.DriverLinkCratePage }))));
const DriverShiftPlannerPage = lazy(() => lazyRetry(() => import("./pages/DriverShiftPlannerPage").then((m) => ({ default: m.DriverShiftPlannerPage }))));
const AdminInventoryPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminInventoryPage").then((m) => ({ default: m.AdminInventoryPage }))));
const AdminOrdersPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminOrdersPage").then((m) => ({ default: m.AdminOrdersPage }))));
const AdminStoragePage = lazy(() => lazyRetry(() => import("./pages/admin/AdminStoragePage").then((m) => ({ default: m.AdminStoragePage }))));
const AdminDashboardPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage }))));
const AdminAuditShelfPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminAuditShelfPage").then((m) => ({ default: m.AdminAuditShelfPage }))));
const AdminExceptionsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminExceptionsPage").then((m) => ({ default: m.AdminExceptionsPage }))));
const AdminPoolsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminPoolsPage").then((m) => ({ default: m.AdminPoolsPage }))));
const AdminSealsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminSealsPage").then((m) => ({ default: m.AdminSealsPage }))));
// Admin command-center routes — see docs/admin/REDESIGN.md for the IA + phases.
const AdminDispatchPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminDispatchPage").then((m) => ({ default: m.AdminDispatchPage }))));
const AdminWarehousePage = lazy(() => lazyRetry(() => import("./pages/admin/AdminWarehousePage").then((m) => ({ default: m.AdminWarehousePage }))));
const AdminCustomersPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminCustomersPage").then((m) => ({ default: m.AdminCustomersPage }))));
const AdminCustomerDetailPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminCustomerDetailPage").then((m) => ({ default: m.AdminCustomerDetailPage }))));
const AdminSupportPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminSupportPage").then((m) => ({ default: m.AdminSupportPage }))));
const AdminMetricsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminMetricsPage").then((m) => ({ default: m.AdminMetricsPage }))));
const AdminDriversPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminDriversPage").then((m) => ({ default: m.AdminDriversPage }))));
const AdminDriverDetailPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminDriverDetailPage").then((m) => ({ default: m.AdminDriverDetailPage }))));
const AdminFeedbackPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminFeedbackPage").then((m) => ({ default: m.AdminFeedbackPage }))));
const AdminWaitlistPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminWaitlistPage").then((m) => ({ default: m.AdminWaitlistPage }))));
const AdminCreatorPartnersPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminCreatorPartnersPage").then((m) => ({ default: m.AdminCreatorPartnersPage }))));
const AdminCreatorDetailPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminCreatorDetailPage").then((m) => ({ default: m.AdminCreatorDetailPage }))));
const AdminServiceAreasPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminServiceAreasPage").then((m) => ({ default: m.AdminServiceAreasPage }))));
const AdminPricingPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminPricingPage").then((m) => ({ default: m.AdminPricingPage }))));
const AdminEmailPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminEmailPage").then((m) => ({ default: m.AdminEmailPage }))));
const AdminChangesPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminChangesPage").then((m) => ({ default: m.AdminChangesPage }))));
const AdminErrorsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminErrorsPage").then((m) => ({ default: m.AdminErrorsPage }))));
const AdminShiftsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminShiftsPage").then((m) => ({ default: m.AdminShiftsPage }))));
const AdminSweepsPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminSweepsPage").then((m) => ({ default: m.AdminSweepsPage }))));
const AdminAuditLogPage = lazy(() => lazyRetry(() => import("./pages/admin/AdminAuditLogPage").then((m) => ({ default: m.AdminAuditLogPage }))));
// The admin frame itself is a LAYOUT ROUTE element, mounted once for the whole
// /admin session (see the route block below). Lazy like the pages so the
// sidebar, topbar and command palette stay out of the customer bundle.
const AdminShell = lazy(() => lazyRetry(() => import("./components/Layout/AdminShell").then((m) => ({ default: m.AdminShell }))));

/**
 * AppShell -- WP-011 Story 1 (AC-001)
 * Inner component that reads auth state to apply pointer-events:none on the
 * shell when the SessionExpiredModal is visible, making all app content inert.
 * SessionExpiredModal is rendered OUTSIDE the inert shell container so it
 * retains pointer-events and can receive the Sign in again click.
 */
function AppShell() {
  const { unauthenticatedReason, isRestoring } = useAuth();
  const isSessionExpired = unauthenticatedReason === "session_expired";

  // Gate the entire shell on auth restoration — prevents the marketing /
  // login page from flashing when a signed-in user refreshes a deep link
  // like /driver/active. SessionRestoreScreen renders its own full-screen
  // spinner.
  if (isRestoring) return <SessionRestoreScreen />;

  return (
    <>
      {/* App shell: pointer-events:none when session expired modal is open (AC-001).
          padding-bottom reserves flow space below the page content so the mobile
          BottomTabBar (position:fixed, ~56px + safe-area) doesn't obscure the last
          row of scrollable content. --bottom-tab-height is 0px on desktop via the
          globals.css media query, so the padding collapses to 0 there. */}
      <div
        className="attic"
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          paddingBottom: "calc(var(--bottom-tab-height) + env(safe-area-inset-bottom, 0px))",
          pointerEvents: isSessionExpired ? "none" : undefined,
        }}
      >
        <ScrollToTop />
        <TopNav />
        {/* SessionExpiredOverlay kept for WP-009 test backward compat (not rendered when modal active) */}
        <SessionExpiredOverlay />
        <Suspense fallback={<LoadingFallback />}>
          <RouteErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              {/* Blocking agreement gate — sits above EVERY authenticated
                  route (including a pasted deep link) and above the
                  onboarding latch, so a material Terms change re-prompts
                  customers who onboarded months ago. Declining drops to
                  read-only rather than locking the account; the server
                  enforces the same split. Passes straight through while no
                  documents are published. */}
              <Route element={<ConsentGate />}>
              {/* Shared — profile is available to every role, it's personal
                  settings + sign-out. */}
              <Route path="/profile" element={<ProfileScreen />} />
              <Route path="/profile/personal" element={<ProfilePersonal />} />
              <Route path="/profile/password" element={<ProfilePassword />} />
              <Route path="/profile/refer" element={<ProfileRefer />} />
              <Route path="/profile/address" element={<ProfileAddress />} />
              <Route path="/profile/preferences" element={<ProfilePreferences />} />
              <Route path="/profile/payment" element={<ProfilePayment />} />
              <Route path="/profile/payment/add" element={<ProfilePaymentAdd />} />
              <Route path="/profile/notifications" element={<ProfileNotifications />} />
              <Route path="/profile/help" element={<ProfileHelp />} />
              <Route path="/profile/legal" element={<ProfileLegal />} />
              <Route path="/profile/delete" element={<ProfileDelete />} />
              <Route path="/profile/close" element={<ProfileClose />} />
              {/* /billing was the canonical billing page pre-redesign.
                  Redirect to /profile/payment so existing dunning emails
                  and bookmarks still resolve. */}
              <Route path="/billing" element={<Navigate to="/profile/payment" replace />} />

              {/* Customer-only routes — drivers/admins get redirected to
                  their role's landing by CustomerRoleRoute. Blocks drivers
                  from typing /dashboard in the URL and landing in a shell
                  that doesn't apply to them. */}
              <Route element={<CustomerRoleRoute />}>
              {/* Customer crate screens are a phone product. On desktop they
                  render badly — the layout was never designed for the viewport
                  and scanning wants a camera — so point people at the app
                  instead of showing them a broken page.

                  Scoped to customer routes ONLY. /profile/*, legal, account
                  deletion, password reset, /login, /driver/* and /admin/* all
                  stay reachable on desktop, because each of those either needs
                  a browser or is the reason the web app exists. `?desktop=1`
                  overrides for the session so support can reproduce a report. */}
              <Route element={<MobileOnlyRoute />}>
                {/* Gated onboarding flow — reachable while incomplete; it
                    redirects completed customers to /dashboard itself. */}
                <Route path="/welcome" element={<WelcomeFlow />} />
                {/* Everything else requires a completed onboarding (address +
                    card + first crate). Incomplete customers bounce to /welcome. */}
                <Route element={<OnboardingCompleteRoute />}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/orders" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/crates/:crateId" element={<CrateDetailScreen />} />
                  <Route path="/search" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/storage" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/my-storage" element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Route>
              </Route>

              {/* Driver-only routes — customers/admins bounce. */}
              <Route element={<DriverRoleRoute />}>
                <Route path="/driver/home" element={<DriverHomePage />} />
                <Route path="/driver/shifts" element={<DriverShiftPlannerPage />} />
                <Route path="/driver/active" element={<DriverActivePage />} />
                {/* Earnings retired for the W-2 fleet — Home is the dashboard now. */}
                <Route path="/driver/earnings" element={<Navigate to="/driver/home" replace />} />
                <Route path="/driver/link-crate" element={<DriverLinkCratePage />} />
                {/* Legacy aliases — kept until external links are updated. */}
                <Route path="/driver/jobs" element={<DriverShiftPlannerPage />} />
                <Route path="/driver/shift/plan" element={<DriverShiftPlannerPage />} />
              </Route>

              {/* Admin-only routes — everyone else bounces.

                  AdminShell is the LAYOUT ROUTE element, not a per-page
                  wrapper: it mounts once on entry to /admin and renders each
                  page below through its own <Outlet/>. Before this, every
                  admin page rendered its own copy of the shell, so a single
                  click through the sidebar tore down the sidebar, the topbar
                  and the ⌘K palette and built them again — the palette
                  re-fetched the whole customer roster on the first open after
                  every navigation, and there was nowhere in the admin that
                  survived long enough to hold a poll. Child paths are
                  relative; the index route is the dashboard. */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminShell />}>
                  <Route index element={<AdminDashboardPage />} />
                  {/* Phase 4 command-center top-level routes (sidebar nav). */}
                  <Route path="dispatch" element={<AdminDispatchPage />} />
                  <Route path="warehouse" element={<AdminWarehousePage />} />
                  <Route path="customers" element={<AdminCustomersPage />} />
                  <Route path="customers/:id" element={<AdminCustomerDetailPage />} />
                  <Route path="support" element={<AdminSupportPage />} />
                  <Route path="metrics" element={<AdminMetricsPage />} />
                  <Route path="drivers" element={<AdminDriversPage />} />
                  <Route path="shifts" element={<AdminShiftsPage />} />
                  <Route path="drivers/:id" element={<AdminDriverDetailPage />} />
                  <Route path="feedback" element={<AdminFeedbackPage />} />
                  <Route path="waitlist" element={<AdminWaitlistPage />} />
                  <Route path="creators" element={<AdminCreatorPartnersPage />} />
                  <Route path="creators/:id" element={<AdminCreatorDetailPage />} />
                  <Route path="service-areas" element={<AdminServiceAreasPage />} />
                  <Route path="pricing" element={<AdminPricingPage />} />
                  <Route path="email" element={<AdminEmailPage />} />
                  <Route path="changes" element={<AdminChangesPage />} />
                  <Route path="sweeps" element={<AdminSweepsPage />} />
                  <Route path="errors" element={<AdminErrorsPage />} />
                  <Route path="audit-log" element={<AdminAuditLogPage />} />
                  {/* Existing operational pages — reachable from sidebar quick links
                      and from inside the new top-level pages. */}
                  <Route path="inventory" element={<AdminInventoryPage />} />
                  <Route path="orders" element={<AdminOrdersPage />} />
                  <Route path="storage" element={<AdminStoragePage />} />
                  {/* Facilities + Shelves merged into the unified Storage explorer; old routes redirect. */}
                  <Route path="shelves" element={<Navigate to="/admin/storage" replace />} />
                  <Route path="facilities" element={<Navigate to="/admin/storage" replace />} />
                  <Route path="audit" element={<AdminAuditShelfPage />} />
                  <Route path="exceptions" element={<AdminExceptionsPage />} />
                  <Route path="pools" element={<AdminPoolsPage />} />
                  <Route path="seals" element={<AdminSealsPage />} />
                </Route>
              </Route>

              {/* Role-based landing — `/` picks the right shell per role. */}
              <Route path="/" element={<RoleLandingRedirect />} />
              <Route path="*" element={<RoleLandingRedirect />} />
              </Route>
            </Route>
            {/* Signup is MOBILE ONLY. The web app is the operator console:
                customers and drivers live in the native app and never need it,
                and an operator account is made by signing up on mobile and
                being promoted from the customer record. Kept as a redirect
                rather than deleted so an old bookmark lands somewhere real. */}
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/help/camera" element={<CameraHelpPage />} />
            {/* Clerk OAuth redirect landing. Clerk's widget sends users
                here after the Google consent handshake completes; the
                component below asks Clerk to finish the session setup,
                then navigates to / where ClerkBridge takes over. */}
            <Route
              path="/sso-callback"
              element={<AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/" signUpFallbackRedirectUrl="/" />}
            />
          </Routes>
          </RouteErrorBoundary>
        </Suspense>
        {/* Mobile primary navigation — hidden on desktop via CSS media query.
            Self-gates on auth so it never shows on /login. */}
        <BottomTabBar />
      </div>
      {/* SessionExpiredModal: WP-011 production modal, rendered outside inert shell (AC-001) */}
      <SessionExpiredModal />
      {/* Reporting + feedback: both self-gate on auth state. ReporterInstaller
          registers the current user + WS-log getters with the module-level
          error reporter; FeedbackWidget renders the floating launcher. */}
      <ReporterInstaller />
      <FeedbackWidget />
      <SyncIndicator />
      {/* Clerk → AuthContext bridge: watches Clerk's sign-in state and
          exchanges for Harper tokens whenever Clerk has a session but
          AuthContext is still unauthenticated. Renders null. */}
      <ClerkBridge />
      {/* Referral redeemer: captures ?ref=<code> on /, stashes
          it through the Clerk handshake, calls /RedeemReferralCode once
          authenticated. Renders null. */}
      <ReferralRedeemer />
    </>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <NativeDeepLinkBridge />
          <NativePushBridge />
          <FormDraftProvider>
            {/* WP-ATTICDEV-013-SNACKBAR: SnackbarProvider inside AuthProvider so any
                integration point (AuthContext watcher, profile save, etc.) can call
                useSnackbar() alongside useAuth() from the same component. SnackbarHost
                renders as a sibling of AppShell to sit above all page content.
                ApiClientSetup moved inside SnackbarProvider (WP-013 US-005) so the
                session-expired watcher can call useSnackbar(). */}
            <RoleVersionProvider>
            {/* SnackbarProvider retained so existing showSnackbar() call
                sites keep compiling, but SnackbarHost is NOT mounted — the
                snackbars collided with celebration banners / pickup
                instruction strips and echoed info already visible on the
                page. All showSnackbar() calls are effectively no-ops now. */}
            <SnackbarProvider>
              <HarperWSProvider>
                <ApiClientSetup />
                <MutationQueueInstaller />
                <AppShell />
              </HarperWSProvider>
            </SnackbarProvider>
            </RoleVersionProvider>
          </FormDraftProvider>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
